/* cc-testimonials — auto-rotating quote carousel.
   Desktop: center-stage scroll-snap row; clicking any side card brings
   it to the middle; rotation never stops (pauses on hover/off-screen).
   Mobile (<=750px): a messy stack of cards — drag/swipe the top card
   away (it flies out on an arc), auto-advances on the same timer. */

class CCTestimonials extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.cc-testimonials__track');
    const real = Array.from(this.querySelectorAll('.cc-testimonials__card'));
    if (!this.track || real.length === 0) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.interval = (parseInt(this.dataset.interval, 10) || 8) * 1000;
    this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion;
    this.stackMode = window.matchMedia('(max-width: 749px)').matches;
    this.n = real.length;

    if (this.stackMode) {
      this.initStack(real);
    } else {
      this.initRow(real);
    }

    this.addEventListener('pointerenter', () => this.pause());
    this.addEventListener('pointerleave', () => this.play());
    document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.play()));
    new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.visible ? this.play() : this.pause();
    }).observe(this);
  }

  /* ---------- desktop: center-stage row ---------- */

  initRow(real) {
    this.cloneCount = this.n > 1 ? Math.min(2, this.n) : 0;
    for (let i = 0; i < this.cloneCount; i++) {
      const front = real[this.n - 1 - i].cloneNode(true);
      const back = real[i].cloneNode(true);
      [front, back].forEach((c) => c.setAttribute('aria-hidden', 'true'));
      this.track.insertBefore(front, this.track.firstChild);
      this.track.appendChild(back);
    }
    this.cards = Array.from(this.track.querySelectorAll('.cc-testimonials__card'));

    // click a side card to bring it center (autoplay keeps going)
    this.track.addEventListener('click', (e) => {
      const card = e.target.closest('.cc-testimonials__card');
      if (!card) return;
      const i = this.cards.indexOf(card);
      if (i !== this.current) {
        this.centerOn(i);
        this.restartTimer();
      }
    });

    this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
    if ('onscrollend' in window) {
      this.track.addEventListener('scrollend', () => this.wrap());
    } else {
      this.track.addEventListener(
        'scroll',
        () => {
          clearTimeout(this.settleTimer);
          this.settleTimer = setTimeout(() => this.wrap(), 120);
        },
        { passive: true }
      );
    }
    window.addEventListener('resize', () => this.queueUpdate());

    this.centerOn(this.cloneCount, true);
    this.update();
    this.buildArrows();
    this.buildRing();
  }

  /* hover-reveal prev/next circles floating over the side cards (Tony 2026-07-11) */
  buildArrows() {
    const host = this.querySelector('.cc-testimonials__inner') || this;
    [[-1, 'Previous testimonial', 'M15 18l-6-6 6-6'], [1, 'Next testimonial', 'M9 6l6 6-6 6']].forEach(([dir, label, path]) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `cc-testimonials__arrow cc-testimonials__arrow--${dir < 0 ? 'prev' : 'next'}`;
      b.setAttribute('aria-label', label);
      b.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${path}"/></svg>`;
      b.addEventListener('click', () => {
        this.centerOn(this.current + dir);
        this.restartTimer();
      });
      host.appendChild(b);
    });
  }

  /* rotation-progress ring under the center card (Tony 2026-07-11).
     CSS animation runs the interval; hover pauses it (autoplay pauses
     on hover too); every advance/resume restarts it in resetRing(). */
  buildRing() {
    if (!this.autoplay) return;
    const host = this.querySelector('.cc-testimonials__inner') || this;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'cc-testimonials__ring');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = '<circle class="cc-testimonials__ring-bg" cx="12" cy="12" r="10"/><circle class="cc-testimonials__ring-fg" cx="12" cy="12" r="10"/>';
    svg.style.setProperty('--cc-ring-t', this.interval + 'ms');
    host.appendChild(svg);
    this.ring = svg;
  }

  resetRing() {
    if (!this.ring) return;
    const fg = this.ring.querySelector('.cc-testimonials__ring-fg');
    fg.style.animation = 'none';
    void fg.getBoundingClientRect(); /* reflow restarts the animation */
    fg.style.animation = '';
  }

  queueUpdate() {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      this.update();
    });
  }

  update() {
    if (this.stackMode) return;
    const trackRect = this.track.getBoundingClientRect();
    const center = trackRect.left + trackRect.width / 2;
    const step = this.cards[0].offsetWidth + 20;
    let best = 0;
    let bestD = Infinity;
    this.cards.forEach((card, i) => {
      const r = card.getBoundingClientRect();
      const d = Math.min(1, Math.abs(r.left + r.width / 2 - center) / step);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
      card.style.setProperty('--cc-o', (1 - 0.45 * d).toFixed(2));
      if (!this.reducedMotion) {
        card.style.setProperty('--cc-s', (1 - 0.12 * d).toFixed(3));
        card.style.setProperty('--cc-ty', (-6 + 16 * d).toFixed(1) + 'px');
      }
    });
    this.cards.forEach((card, i) => card.classList.toggle('is-center', i === best));
    this.current = best;
  }

  wrap() {
    if (this.stackMode) return;
    if (this.current < this.cloneCount) {
      this.centerOn(this.current + this.n, true);
    } else if (this.current >= this.cloneCount + this.n) {
      this.centerOn(this.current - this.n, true);
    }
  }

  centerOn(i, instant = false) {
    if (!instant) this.resetRing();
    const card = this.cards[Math.max(0, Math.min(i, this.cards.length - 1))];
    const left = card.offsetLeft - (this.track.clientWidth - card.offsetWidth) / 2;
    if (instant) {
      const prev = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollTo({ left });
      this.track.style.scrollBehavior = prev;
      this.queueUpdate();
    } else {
      this.track.scrollTo({ left });
    }
  }

  /* ---------- mobile: messy stack ---------- */

  initStack(real) {
    this.classList.add('cc-testimonials--stack');
    this.cards = real;
    this.order = real.map((_, i) => i);

    // container height = tallest card + stack offsets
    requestAnimationFrame(() => {
      const h = Math.max(...this.cards.map((c) => c.offsetHeight));
      this.track.style.height = h + 40 + 'px';
      this.applyStack();
    });

    // drag the top card
    this.track.addEventListener('pointerdown', (e) => {
      const top = this.cards[this.order[0]];
      if (!top.contains(e.target)) return;
      this.dragging = { x: e.clientX, card: top };
      top.setPointerCapture?.(e.pointerId);
      top.classList.add('is-dragging');
      this.pause();
    });
    this.track.addEventListener('pointermove', (e) => {
      if (!this.dragging) return;
      const dx = e.clientX - this.dragging.x;
      this.dragging.card.style.transform = `translateX(${dx}px) rotate(${dx * 0.06}deg)`;
    });
    const release = (e) => {
      if (!this.dragging) return;
      const dx = e.clientX - this.dragging.x;
      const card = this.dragging.card;
      card.classList.remove('is-dragging');
      this.dragging = null;
      card.style.transform = '';
      if (Math.abs(dx) > 70) {
        this.advanceStack(dx < 0 ? -1 : 1);
      }
      this.play();
    };
    this.track.addEventListener('pointerup', release);
    this.track.addEventListener('pointercancel', release);
  }

  applyStack() {
    const messy = [
      { r: -1.5, x: 0, y: 0 },
      { r: 2.2, x: 6, y: 12 },
      { r: -2.8, x: -7, y: 22 },
      { r: 1.6, x: 4, y: 30 },
    ];
    this.order.forEach((cardIdx, pos) => {
      const card = this.cards[cardIdx];
      const p = messy[Math.min(pos, 3)];
      card.style.zIndex = this.n - pos;
      card.style.setProperty('--st-r', p.r + 'deg');
      card.style.setProperty('--st-x', p.x + 'px');
      card.style.setProperty('--st-y', p.y + 'px');
      card.classList.toggle('is-buried', pos > 3);
      card.classList.toggle('is-top', pos === 0);
    });
  }

  advanceStack(dir = -1) {
    const top = this.cards[this.order[0]];
    top.style.setProperty('--fly', dir);
    top.classList.add('is-flying');
    setTimeout(() => {
      top.classList.remove('is-flying');
      this.order.push(this.order.shift());
      this.applyStack();
    }, 330);
  }

  /* ---------- shared timer ---------- */

  restartTimer() {
    this.pause();
    this.play();
  }

  play() {
    if (!this.autoplay || !this.visible || this.timer) return;
    if (!this.stackMode) this.resetRing(); /* ring restarts in sync with the fresh interval */
    this.timer = setInterval(() => {
      this.stackMode ? this.advanceStack(-1) : this.centerOn(this.current + 1);
    }, this.interval);
  }

  pause() {
    clearInterval(this.timer);
    this.timer = null;
  }
}

customElements.define('cc-testimonials', CCTestimonials);
